/**
 * Condition evaluation for IfFormatter.
 * Conditions are always created on the backend (Java: com.anneca.intouch.api.agenda.format.cond)
 * and deserialized + evaluated here.
 */
import type {ConditionData} from "@t/agenda/CellFormatter.ts";
import {hasAccess, ObjectAccessFlag} from "@t/security/ObjectAccess.ts";

function valueAt(index: number, data: any[]): any {
	if (index < 0 || index >= data.length) return null;
	return data[index];
}

function truthful(value: any): boolean {
	if (value == null) return false;
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value !== 0;
	if (typeof value === "string") return value.length > 0;
	return true;
}

function compare(left: any, right: any, check: (cmp: number) => boolean): boolean {
	if (left == null) return right == null;
	if (right == null) return false;

	if (typeof left === "number" && typeof right === "number") {
		return check(left < right ? -1 : left > right ? 1 : 0);
	}
	if (typeof left === "string" && typeof right === "string") {
		return check(left < right ? -1 : left > right ? 1 : 0);
	}
	return false;
}

function resolveRight(c: ConditionData, data: any[]): any {
	if (typeof c.right === 'number') {
		// Index to the data
		return valueAt(c.right, data);
	} else if (typeof c.right === 'object') {
		switch (c.right.type) {
			case "STATIC":
				return c.right.value;
			case "GETTER":
				if (typeof c.right.expression === "function") return c.right.expression();
				// Compile
				c.right.expression = new Function("return ("+c.right.expression+")");
				return c.right.expression();
		}
	} else return c.right;	// other types are considered static
}

function objectHasAccessFlag(ro: object, flag: ObjectAccessFlag):boolean {
	return hasAccess(ro, flag);
}

export function testCondition(c: ConditionData, ro: object, data: any[]): boolean {
	switch (c.op) {
		case "TRUTHFUL":
			return truthful(valueAt(c.index ?? 0, data));
		case "NOT":
			return !testCondition(c.test!, ro, data);
		case "AND":
			return testCondition(c.left as ConditionData, ro, data) && testCondition(c.right as ConditionData, ro, data);
		case "OR":
			return testCondition(c.left as ConditionData, ro, data) || testCondition(c.right as ConditionData, ro, data);
		case "EQ":
			return compare(valueAt(c.left as number, data), resolveRight(c, data), cmp => cmp === 0);
		case "LT":
			return compare(valueAt(c.left as number, data), resolveRight(c, data), cmp => cmp < 0);
		case "GT":
			return compare(valueAt(c.left as number, data), resolveRight(c, data), cmp => cmp > 0);
		case "LTE":
			return compare(valueAt(c.left as number, data), resolveRight(c, data), cmp => cmp <= 0);
		case "GTE":
			return compare(valueAt(c.left as number, data), resolveRight(c, data), cmp => cmp >= 0);
		case "ACCESS":
			return objectHasAccessFlag(ro, c.flag);
	}
}
